/*
  "Graded in": which languages a game's, a set's or a card's graded cards were in, as one
  horizontal bar per language (docs/specs/pop-report-followups.md R15, R16, R15b, R16b,
  D9, D14, D15).

  The numbers are the backend's /v2/catalog/gradedLanguages, already normalised to the
  published names (R8). Gradings with no recorded language count as English on screen
  (D15, lib/gradedLanguages), and the rows are re-sorted after that fold. A bar's length
  is its count against the LONGEST bar, so the top row is always full width; the share
  printed beside it is against the whole `total`, which is why the shares need not sum to
  100 once rounded.

  Two variants. The full chart is a section of its own with the page heading pattern: the
  set page under SetHeader, and the card page under the grade mix. The compact one sits in
  the game header beside the language toggle (D14): the top three languages, an Other row
  summing the rest, and an "All languages" button that opens the full list in place. A
  highlighted language outside the top three is drawn as a fourth bar, before Other.

  Plain HTML on skin tokens, no chart library. Every bar is one gold, from the readable
  gold token rather than the fill: goldText is bright on the dark grounds and deep amber
  on paper, so the highlighted bar stands out in both modes, and the base bars are the
  same gold at 60% over the track (40% was faint in the light palettes). goldMuted, the
  hairline's token, was the obvious base, but in every light block it is a pale tint that
  all but vanishes on the raised track.
  The highlight (D9) is the toggle's language on the game page; the set and card pages
  have none, and Other is never highlighted.

  Every count and share is printed, so there is no hover layer: a tooltip would only
  repeat the row. Each bar carries the row's whole sentence as its label, and the
  printed text beside it is hidden from screen readers so the row is read once.

  The chart renders nothing while loading, on error, or when nothing has been graded
  (`total` 0). With D15 there is no longer a case of gradings with nothing to draw.
*/

import { useState } from 'react';

import { CONTROL_LABEL, FOCUS_RING } from 'components/styles';
import { formatCount } from 'lib';
import { COMPACT_LIMIT, compactLanguages, foldUnrecordedIntoEnglish } from 'lib/gradedLanguages';
import type { LanguageCount } from 'lib/gradedLanguages';
import { useGetGradedLanguagesQuery } from 'services/pcgPublicV2';

export type ChartHighlight = 'English' | 'Japanese' | 'Chinese' | null;

/* Written out whole for Tailwind's scanner; the test pins them. */
export const BAR_TRACK = 'bg-pcg-raised';
export const BAR_BASE = 'bg-pcg-goldText/60';
export const BAR_HIGHLIGHT = 'bg-pcg-goldText';

/* A count's share of the total, to the nearest whole percent; `<1%` for a sliver. */
export const shareLabel = (count: number, total: number): string => {
  if (count <= 0 || total <= 0) {
    return '0%';
  }
  const share = (count / total) * 100;
  return share < 0.5 ? '<1%' : `${Math.round(share)}%`;
};

interface GradedLanguagesChartProps {
  gameId?: number;
  setId?: number;
  productId?: number;
  highlight?: ChartHighlight;
  variant?: 'full' | 'compact';
  /* Spacing from the caller, on the section itself, so a hidden chart leaves no gap. */
  className?: string;
}

const HEADING_ID = 'graded-in-heading';
const COMPACT_HEADING_ID = 'graded-in-compact-heading';
const ALL_LANGUAGES_ID = 'graded-in-all-languages';

interface BarRowProps {
  name: string;
  /* What the bar is read as; Other says "Other languages". */
  spoken?: string;
  count: number;
  total: number;
  longest: number;
  highlighted: boolean;
  /* The compact rows are tighter and print the share alone; the label still has both. */
  compact: boolean;
}

const BarRow = ({ name, spoken = name, count, total, longest, highlighted, compact }: BarRowProps) => {
  const share = shareLabel(count, total);
  return (
    <li
      className={
        compact
          ? 'grid grid-cols-[4.5rem_minmax(0,1fr)_auto] items-center gap-2 text-xs'
          : 'grid grid-cols-[5.5rem_minmax(0,1fr)_auto] items-center gap-3 text-sm sm:grid-cols-[7rem_minmax(0,1fr)_auto]'
      }
    >
      <span aria-hidden="true" className="truncate font-semibold text-pcg-text">
        {name}
      </span>
      <span className={`${BAR_TRACK} ${compact ? 'h-2' : 'h-3'} overflow-hidden rounded-r`}>
        <span
          role="img"
          aria-label={`${spoken}: ${formatCount(count)} cards, ${share}`}
          className={`${highlighted ? BAR_HIGHLIGHT : BAR_BASE} block h-full rounded-r`}
          style={{ width: `${longest > 0 ? (count / longest) * 100 : 0}%`, minWidth: '4px' }}
        />
      </span>
      {compact ? (
        <strong aria-hidden="true" className="min-w-[2.25rem] text-right font-semibold tabular-nums text-pcg-text">
          {share}
        </strong>
      ) : (
        <span aria-hidden="true" className="min-w-[6.5rem] whitespace-nowrap text-right tabular-nums text-pcg-textMuted sm:min-w-[8rem]">
          {formatCount(count)} · <strong className="font-semibold text-pcg-text">{share}</strong>
        </span>
      )}
    </li>
  );
};

const longestOf = (counts: number[]): number => Math.max(0, ...counts);

const GradedLanguagesChart = ({
  gameId,
  setId,
  productId,
  highlight = null,
  variant = 'full',
  className = '',
}: GradedLanguagesChartProps) => {
  const [showAll, setShowAll] = useState(false);

  /* `currentData` so one game's bars are never drawn under the next game's page. */
  const { currentData, isError } = useGetGradedLanguagesQuery(
    { gameId, setId, productId },
    { skip: gameId === undefined && setId === undefined && productId === undefined }
  );

  if (isError || !currentData) {
    return null;
  }
  const { total } = currentData;
  const rows = foldUnrecordedIntoEnglish(currentData.languages, currentData.notRecorded);
  if (total <= 0 || rows.length === 0) {
    return null;
  }

  const fullRows = (compact: boolean, longest: number) =>
    rows.map((row: LanguageCount) => (
      <BarRow
        key={row.language}
        name={row.language}
        count={row.count}
        total={total}
        longest={longest}
        highlighted={row.language === highlight}
        compact={compact}
      />
    ));

  if (variant === 'compact') {
    const { shown, other } = compactLanguages(rows, COMPACT_LIMIT, highlight);
    const longest = longestOf([...shown.map(row => row.count), other]);
    return (
      <section
        aria-labelledby={COMPACT_HEADING_ID}
        className={`rounded-xl border border-pcg-borderSoft px-3 py-2.5 ${className}`}
      >
        <h2 id={COMPACT_HEADING_ID} className={CONTROL_LABEL}>
          Graded in
        </h2>
        <ul aria-label="Top languages" className="mt-2 flex flex-col gap-1.5">
          {shown.map(row => (
            <BarRow
              key={row.language}
              name={row.language}
              count={row.count}
              total={total}
              longest={longest}
              highlighted={row.language === highlight}
              compact
            />
          ))}
          {other > 0 ? (
            <BarRow
              name="Other"
              spoken="Other languages"
              count={other}
              total={total}
              longest={longest}
              highlighted={false}
              compact
            />
          ) : null}
        </ul>
        {/* The full list, only when Other is hiding some of it; GameView's Archived sets idiom. */}
        {other > 0 ? (
          <>
            <button
              type="button"
              aria-expanded={showAll}
              aria-controls={ALL_LANGUAGES_ID}
              onClick={() => setShowAll(current => !current)}
              className={`mt-2 rounded-sm text-xs font-semibold text-pcg-goldText hover:underline ${FOCUS_RING}`}
            >
              All languages
            </button>
            {/* The display class must follow showAll too: Tailwind's `flex` outranks the
                `hidden` attribute's UA style, so a static `flex` left the list always shown. */}
            <ul
              id={ALL_LANGUAGES_ID}
              hidden={!showAll}
              className={`mt-2 flex-col gap-1.5 ${showAll ? 'flex' : 'hidden'}`}
            >
              {fullRows(true, longestOf(rows.map(row => row.count)))}
            </ul>
          </>
        ) : null}
      </section>
    );
  }

  return (
    <section aria-labelledby={HEADING_ID} className={className}>
      <div className="mb-4 flex items-center gap-3">
        <h2 id={HEADING_ID} className="font-display text-lg font-semibold text-pcg-text sm:text-xl">
          Graded in
        </h2>
        <span aria-hidden="true" className="h-px flex-1 bg-pcg-goldMuted" />
        <span className="shrink-0 text-xs uppercase tracking-wide text-pcg-textMuted">
          By card language
        </span>
      </div>
      <ul className="flex flex-col gap-2.5">{fullRows(false, longestOf(rows.map(row => row.count)))}</ul>
    </section>
  );
};

export default GradedLanguagesChart;
